import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, MapPin, ArrowRight, ShoppingBag, KeyRound, BedDouble, BadgeCheck,
  Sparkles, MessagesSquare, IdCard, Users2, Target, Handshake, CalendarDays,
  Briefcase, Home, Building2, ShieldCheck, Play, RotateCcw, ChevronLeft, ChevronRight,
} from "lucide-react";
import { PublicHeader } from "@/components/PublicHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FeaturedProsCarousel } from "@/components/featured/FeaturedProsCarousel";
import { Button } from "@/components/ui/button";
import { api, priceLabel } from "@/lib/api";
import { Seo, organizationJsonLd, websiteJsonLd } from "@/components/Seo";
import { PROPERTY_TYPE_GROUPS, propertyTypeLabel } from "@/lib/taxonomy";

const IMG = {
  dusk: "https://images.unsplash.com/photo-1581216340441-d47cad9210a4?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600",
};

const TABS = [
  { key: "For Sale", label: "Buy", icon: ShoppingBag },
  { key: "For Rent", label: "Rent", icon: KeyRound },
  { key: "Unit Hosting", label: "Stays", icon: BedDouble },
];

const PRICE_RANGES = {
  "For Sale": [
    { label: "Any price", min: "", max: "" },
    { label: "Under ₱3M", min: "", max: "3000000" },
    { label: "₱3M – ₱6M", min: "3000000", max: "6000000" },
    { label: "₱6M – ₱10M", min: "6000000", max: "10000000" },
    { label: "₱10M – ₱20M", min: "10000000", max: "20000000" },
    { label: "₱20M+", min: "20000000", max: "" },
  ],
  "For Rent": [
    { label: "Any price", min: "", max: "" },
    { label: "Under ₱20k/mo", min: "", max: "20000" },
    { label: "₱20k – ₱40k/mo", min: "20000", max: "40000" },
    { label: "₱40k – ₱80k/mo", min: "40000", max: "80000" },
    { label: "₱80k+/mo", min: "80000", max: "" },
  ],
  "Unit Hosting": [
    { label: "Any price", min: "", max: "" },
    { label: "Under ₱3k/night", min: "", max: "3000" },
    { label: "₱3k – ₱6k/night", min: "3000", max: "6000" },
    { label: "₱6k – ₱12k/night", min: "6000", max: "12000" },
    { label: "₱12k+/night", min: "12000", max: "" },
  ],
};

// --- Compact location typeahead (reuses the public /geo/search endpoint) ---
function LocationBox({ value, onPick, testid }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/geo/search`, { params: { q: q.trim(), limit: 10 } });
        setResults(data.results || []); setOpen(true);
      } catch { setResults([]); }
    }, 280);
    return () => clearTimeout(timer.current);
  }, [q]);
  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  if (value?.name) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 h-12" data-testid={`${testid}-current`}>
        <MapPin className="h-4 w-4 text-[#0F2C59] shrink-0" />
        <span className="text-sm font-medium text-slate-700 truncate">{value.name}</span>
        <button type="button" data-testid={`${testid}-clear`} onClick={() => onPick({ code: "", name: "" })} className="ml-auto text-xs text-slate-500 hover:text-slate-800">Clear</button>
      </div>
    );
  }
  return (
    <div ref={boxRef} className="relative">
      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input
        data-testid={testid}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        placeholder="City or area"
        className="w-full h-12 pl-9 pr-3 rounded-xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#C79A3B]"
      />
      {open && results.length > 0 && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-results`}>
          {results.map((r) => (
            <button key={r.code || r.name} type="button" onClick={() => { onPick({ code: r.code || "", name: r.name }); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />
              <span className="truncate">{r.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const HERO_VIDEO = `${process.env.PUBLIC_URL || ""}/media/propconnect-hero-v2.mp4`;
const HERO_VIDEO_WEBM = `${process.env.PUBLIC_URL || ""}/media/propconnect-hero-v2.webm`;
const HERO_POSTER = `${process.env.PUBLIC_URL || ""}/media/propconnect-hero-v2-poster.jpg`;

// Public homepage hero video: portrait 9:16, click-to-play with sound, no autoplay/loop.
function HeroVideo() {
  const videoRef = useRef(null);
  const [started, setStarted] = useState(false);
  const [failed, setFailed] = useState(false);

  const start = () => {
    setFailed(false);
    setStarted(true);
    const v = videoRef.current;
    if (v) {
      v.load();
      const p = v.play();
      if (p && p.catch) p.catch(() => { /* user can press controls */ });
    }
  };

  const retry = () => {
    setFailed(false);
    const v = videoRef.current;
    if (v) { v.load(); const p = v.play(); if (p && p.catch) p.catch(() => setFailed(true)); }
  };

  return (
    <div className="w-full flex flex-col items-center" data-testid="hero-video-wrap">
      <div
        className="relative mx-auto rounded-3xl overflow-hidden bg-black shadow-[0_24px_60px_-24px_rgba(23,54,93,0.45)] ring-1 ring-white/50 aspect-[9/16] w-[min(72vw,270px)] sm:w-[240px] max-h-[480px] lg:w-[405px] lg:h-[720px] lg:max-h-none"
        data-testid="hero-video-frame"
      >
        <video
          ref={videoRef}
          data-testid="hero-video"
          className="absolute inset-0 w-full h-full object-contain"
          poster={HERO_POSTER}
          preload="none"
          playsInline
          controls={started && !failed}
          onPlaying={() => setFailed(false)}
          onError={() => { const v = videoRef.current; if (!v || (v.readyState < 2 && !v.currentTime)) setFailed(true); }}
          aria-label="PropertyConnect introduction video"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
          <source src={HERO_VIDEO_WEBM} type="video/webm" />
        </video>

        {(!started || failed) && (
          <button
            type="button"
            onClick={failed ? retry : start}
            data-testid={failed ? "hero-video-retry" : "hero-video-play"}
            className="group absolute inset-0 flex items-center justify-center bg-black/10 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#C79A3B]/70"
            aria-label={failed ? "Playback failed. Retry PropertyConnect video" : "Watch PropertyConnect · 44 seconds"}
          >
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-lg transition-transform group-hover:scale-105">
              {failed ? <RotateCcw className="h-7 w-7 text-[#0F2C59]" /> : <Play className="h-7 w-7 text-[#0F2C59] translate-x-0.5" fill="currentColor" />}
            </span>
          </button>
        )}
      </div>

      {(!started || failed) && (
        <button
          type="button"
          onClick={failed ? retry : start}
          data-testid="hero-video-label"
          className="mt-4 text-[13px] font-semibold text-[#0F2C59] hover:text-[#C79A3B] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C79A3B] rounded"
        >
          {failed ? "Playback failed — tap to retry" : "Watch PropertyConnect · 44 seconds"}
        </button>
      )}
    </div>
  );
}

/* ---------------- Public Playbook (guest configuration) ---------------- */
const PB_IMG = (name) => `${process.env.PUBLIC_URL || ""}/playbook/${name}`;

const PUBLIC_SLIDES = [
  {
    goal: "find-property", icon: Search, goalLabel: "Find a Property", image: "find-property.webp",
    heading: "Find a place that fits your next move.",
    copy: "Explore properties for Sale, Rent, or Stays. Compare locations, prices, and property details.",
    steps: ["Choose Buy, Rent, or Stays", "Refine your search", "Explore property details"],
    primary: { label: "Explore Marketplace", to: "/marketplace" },
  },
  {
    goal: "offer-property", icon: Home, goalLabel: "Offer a Property", image: "offer-property.webp",
    heading: "Give your property a place in the network.",
    copy: "Create an account and explore the listing workflow for sharing your property inventory.",
    steps: ["Create your account", "Complete property details", "Share your listing link"],
    primary: { label: "Join to Get Started", to: "/register" },
    secondary: { label: "Sign In", to: "/login" },
  },
  {
    goal: "find-demand", icon: Target, goalLabel: "Find Demand", image: "slide3.webp",
    heading: "Someone may be looking for your property.",
    copy: "Members can browse active Buyer Needs. Eligible verified professionals can respond with suitable inventory.",
    steps: ["Join the network", "Explore requirements", "Respond when eligible"],
    primary: { label: "Join the Network", to: "/register" },
    secondary: { label: "Sign In", to: "/login" },
  },
  {
    goal: "build-connections", icon: Handshake, goalLabel: "Build Connections", image: "slide4.webp",
    heading: "Your next opportunity may start with a connection.",
    copy: "Explore published professional profiles and join the network to build real estate relationships.",
    steps: ["Explore profiles", "Review roles and service areas", "Connect as a member"],
    // No guest-safe professional index exists; per spec, fall back to Join the Network.
    primary: { label: "Join the Network", to: "/register" },
    secondary: { label: "Sign In", to: "/login" },
  },
  {
    goal: "events", icon: CalendarDays, goalLabel: "Events", image: "slide6.webp",
    heading: "Be where new opportunities begin.",
    copy: "Discover open houses, project presentations, and real estate events across the network.",
    steps: ["Explore events", "Review dates and venues", "RSVP under existing access rules"],
    primary: { label: "Explore Events", to: "/marketplace?mode=events" },
    secondary: { label: "Join to Participate", to: "/register" },
  },
  {
    goal: "recruitment", icon: Briefcase, goalLabel: "Recruitment", image: "slide7.webp",
    heading: "Find your next team—or your next team member.",
    copy: "Explore recruitment opportunities. Join to apply or access recruitment posting, subject to existing eligibility.",
    steps: ["Explore opportunities", "Review role details", "Apply or recruit when eligible"],
    primary: { label: "Explore Recruitment", to: "/marketplace?mode=recruitment" },
    secondary: { label: "Join the Network", to: "/register" },
  },
];

function PbAction({ action, variant, testid }) {
  const base = "inline-flex items-center justify-center gap-2 rounded-xl h-11 px-5 text-sm font-semibold transition-colors";
  const cls = variant === "primary"
    ? `${base} bg-[#0F2C59] text-white hover:bg-[#0F2C59]/90`
    : `${base} border border-[#0F2C59]/25 text-[#0F2C59] hover:bg-[#0F2C59]/5`;
  return (
    <Link to={action.to} data-testid={testid} className={cls}>
      {action.label}{variant === "primary" && <ArrowRight className="h-4 w-4" />}
    </Link>
  );
}

function PublicPlaybook() {
  const [index, setIndex] = useState(0);
  const touchX = useRef(null);
  const slide = PUBLIC_SLIDES[index];
  const go = useCallback((i) => setIndex(((i % PUBLIC_SLIDES.length) + PUBLIC_SLIDES.length) % PUBLIC_SLIDES.length), []);
  const prev = useCallback(() => go(index - 1), [go, index]);
  const next = useCallback(() => go(index + 1), [go, index]);
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
  };
  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (dx > 40) prev(); else if (dx < -40) next();
    touchX.current = null;
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16 w-full" data-testid="public-playbook-section">
      <div className="max-w-2xl mb-7">
        <p className="text-xs font-semibold tracking-[0.18em] text-[#C79A3B] uppercase mb-2">The Playbook</p>
        <h2 className="font-display font-bold text-[#0F2C59] text-2xl sm:text-3xl">What brings you to PropertyConnect?</h2>
        <p className="text-slate-600 mt-2 text-sm sm:text-base leading-relaxed">Choose a goal and discover how the network can help.</p>
      </div>

      {/* Goal selectors */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="public-goal-selectors" role="tablist" aria-label="Choose a goal">
        {PUBLIC_SLIDES.map((s, i) => {
          const active = i === index;
          return (
            <button key={s.goal} type="button" role="tab" aria-selected={active}
              data-testid={`public-goal-${s.goal}`} onClick={() => go(i)}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors border ${active ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-white text-[#17365D] border-[#DCE5EF] hover:bg-[#F2F8FE]"}`}>
              <s.icon className="h-4 w-4" />{s.goalLabel}
            </button>
          );
        })}
      </div>

      {/* Carousel */}
      <div
        className="rounded-3xl border border-[#E3EAF3] bg-white p-4 sm:p-6 lg:p-8 shadow-[0_18px_50px_-30px_rgba(23,54,93,0.4)]"
        data-testid="public-playbook-carousel"
        role="group" aria-roledescription="carousel" aria-label="PropertyConnect Playbook"
        tabIndex={0} onKeyDown={onKeyDown}
      >
        <div
          key={index}
          className="grid lg:grid-cols-2 gap-6 lg:gap-10 items-center animate-in fade-in duration-300 motion-reduce:animate-none"
          data-testid="public-playbook-slide" data-slide-index={index}
          onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
        >
          {/* Artwork — kept separate from text and controls */}
          <div className="rounded-2xl overflow-hidden bg-[#EDF3FA] ring-1 ring-[#E3EAF3] order-1">
            <img src={PB_IMG(slide.image)} alt={slide.heading} loading="eager"
              className="w-full h-auto block" data-testid={`public-slide-image-${slide.goal}`} />
          </div>
          {/* Content */}
          <div className="order-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#C79A3B]">{slide.goalLabel}</div>
            <h3 className="font-display text-2xl sm:text-3xl font-bold text-[#17365D] mt-2 leading-tight" data-testid="public-slide-heading">{slide.heading}</h3>
            <p className="text-slate-600 text-sm sm:text-base mt-3 leading-relaxed">{slide.copy}</p>
            <ol className="mt-5 space-y-2" data-testid="public-slide-steps">
              {slide.steps.map((s, i) => (
                <li key={s} className="flex items-center gap-3 text-sm text-slate-700">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#EDF3FA] text-[#17365D] text-xs font-bold">{i + 1}</span>{s}
                </li>
              ))}
            </ol>
            <div className="mt-7 flex flex-col sm:flex-row flex-wrap gap-3">
              <PbAction action={slide.primary} variant="primary" testid="public-slide-primary" />
              {slide.secondary && <PbAction action={slide.secondary} variant="secondary" testid="public-slide-secondary" />}
            </div>
          </div>
        </div>

        {/* Controls — outside artwork */}
        <div className="mt-7 flex items-center justify-between gap-3 border-t border-[#EDF3FA] pt-5">
          <button type="button" onClick={prev} data-testid="public-carousel-prev" aria-label="Previous slide"
            className="h-10 w-10 rounded-full border border-[#DCE5EF] text-[#17365D] flex items-center justify-center hover:bg-[#F2F8FE] transition-colors">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2" data-testid="public-slide-selectors">
            {PUBLIC_SLIDES.map((s, i) => (
              <button key={s.goal} type="button" onClick={() => go(i)}
                data-testid={`public-slide-selector-${i + 1}`} aria-label={`Go to slide ${i + 1}: ${s.goalLabel}`} aria-current={i === index}
                className={`h-2.5 rounded-full transition-all ${i === index ? "w-7 bg-[#0F2C59]" : "w-2.5 bg-[#CBD9EA] hover:bg-[#9FB6D4]"}`} />
            ))}
          </div>
          <button type="button" onClick={next} data-testid="public-carousel-next" aria-label="Next slide"
            className="h-10 w-10 rounded-full border border-[#DCE5EF] text-[#17365D] flex items-center justify-center hover:bg-[#F2F8FE] transition-colors">
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Compact live network preview ---------------- */
// Targeted guard to keep QA/demo fixtures out of the guest-facing preview.
const FIXTURE_RE = /^\s*(\[?QA|TEST[_\s]|QAFIX|QADP)/i;
const notFixture = (x) => x && x.title && !FIXTURE_RE.test(x.title);
const peso = (n) => `₱${Number(n || 0).toLocaleString()}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-PH", { timeZone: "Asia/Manila", month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };

function propThumb(p) {
  if (Array.isArray(p.photo_urls) && p.photo_urls[0]) return p.photo_urls[0].thumb_url || p.photo_urls[0].url;
  return p.image || null;
}
function propPrice(p) {
  if (p.listing_type === "Unit Hosting") return `${peso(p.nightly_rate)} / night`;
  try { return priceLabel(p); } catch { return peso(p.price); }
}

function NetworkPreview() {
  const [props, setProps] = useState([]);
  const [event, setEvent] = useState(null);
  useEffect(() => {
    api.get("/public/listings", { params: { page_size: 8, sort: "newest" } })
      .then((r) => setProps(((r.data && r.data.items) || []).filter(notFixture).slice(0, 2)))
      .catch(() => setProps([]));
    api.get("/public/events")
      .then((r) => { const arr = (Array.isArray(r.data) ? r.data : (r.data?.items || [])).filter(notFixture); setEvent(arr[0] || null); })
      .catch(() => setEvent(null));
  }, []);

  const hasAny = props.length > 0 || event;
  if (!hasAny) return null;

  return (
    <section className="bg-slate-50 border-y border-slate-200" data-testid="network-preview-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-7">
          <div>
            <p className="text-xs font-semibold tracking-[0.18em] text-[#C79A3B] uppercase mb-2">Live now</p>
            <h2 className="font-display font-bold text-[#0F2C59] text-2xl sm:text-3xl">Explore the Network</h2>
          </div>
          <div className="flex items-center gap-4 text-sm font-semibold">
            <Link to="/marketplace" data-testid="preview-view-marketplace" className="text-[#0F2C59] hover:text-[#C79A3B] inline-flex items-center gap-1.5">View Marketplace <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/marketplace?mode=events" data-testid="preview-browse-events" className="text-[#0F2C59] hover:text-[#C79A3B] inline-flex items-center gap-1.5">Browse Events <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="network-preview-grid">
          {props.map((p) => {
            const thumb = propThumb(p);
            return (
              <Link key={p.id} to={`/listing/${p.id}`} data-testid={`preview-property-${p.id}`}
                className="group flex gap-3 rounded-2xl border border-slate-200 bg-white p-3 hover:shadow-md hover:border-[#C79A3B]/40 transition-all">
                <div className="h-20 w-24 shrink-0 rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center ring-1 ring-slate-100">
                  {thumb ? <img src={thumb} alt={p.title} loading="lazy" className="w-full h-full object-contain" /> : <Building2 className="h-7 w-7 text-slate-300" />}
                </div>
                <div className="min-w-0 flex flex-col justify-center">
                  <div className="font-display font-bold text-[#0F2C59] text-sm truncate">{propPrice(p)}</div>
                  <div className="text-xs text-slate-700 line-clamp-2 mt-0.5">{p.title}</div>
                  <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{p.city || "Philippines"}{p.project ? ` · ${p.project}` : ""}</span></div>
                </div>
              </Link>
            );
          })}

          {event && (
            <Link to={`/event/${event.id}`} data-testid={`preview-event-${event.id}`}
              className="group flex gap-3 rounded-2xl border border-slate-200 bg-white p-3 hover:shadow-md hover:border-[#C79A3B]/40 transition-all">
              <div className="h-20 w-24 shrink-0 rounded-xl overflow-hidden bg-slate-100 flex items-center justify-center ring-1 ring-slate-100">
                {event.cover_image ? <img src={event.cover_image} alt={event.title} loading="lazy" className="w-full h-full object-contain" /> : <CalendarDays className="h-7 w-7 text-[#C79A3B]/60" />}
              </div>
              <div className="min-w-0 flex flex-col justify-center">
                <div className="text-[11px] font-bold uppercase tracking-wide text-[#C79A3B]">{event.event_type_label || "Event"}</div>
                <div className="text-sm font-semibold text-[#0F2C59] line-clamp-2 mt-0.5">{event.title}</div>
                <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1"><CalendarDays className="h-3 w-3 shrink-0" />{fmtDate(event.start_at)}{(event.venue || event.city) ? ` · ${event.venue || event.city}` : ""}</div>
              </div>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const [tab, setTab] = useState("For Sale");
  const [loc, setLoc] = useState({ code: "", name: "" });
  const [propType, setPropType] = useState("");
  const [priceIdx, setPriceIdx] = useState(0);

  useEffect(() => { setPriceIdx(0); }, [tab]);

  const submitSearch = (e) => {
    e && e.preventDefault();
    const p = new URLSearchParams();
    if (tab !== "For Sale") p.set("type", tab);
    if (loc.code) { p.set("location_code", loc.code); if (loc.name) p.set("loc", loc.name); }
    else if (loc.name) p.set("loc", loc.name);
    if (propType) p.set("property_type", propType);
    const pr = (PRICE_RANGES[tab] || [])[priceIdx];
    if (pr && pr.min) p.set("min_price", pr.min);
    if (pr && pr.max) p.set("max_price", pr.max);
    nav(`/marketplace${p.toString() ? `?${p.toString()}` : ""}`);
  };

  const discover = (t) => nav(`/marketplace${t !== "For Sale" ? `?type=${encodeURIComponent(t)}` : ""}`);

  const TRUST = [
    { icon: IdCard, t: "Published Profiles", d: "Explore professional roles and service areas." },
    { icon: BadgeCheck, t: "Verification Signals", d: "Review the verification information shown on profiles." },
    { icon: Users2, t: "Professional Connections", d: "Build relationships through the network." },
  ];
  const TOOLS = [
    { icon: Sparkles, t: "Listing Assistant", d: "Create clearer listing content with AI support." },
    { icon: Target, t: "Smart Matching", d: "Surface relevant connections between properties and Buyer Needs." },
    { icon: MessagesSquare, t: "Sales Coach", d: "Get guidance on opportunities that deserve attention." },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="landing-page">
      <Seo
        title="PropertyConnect | Philippine Real Estate Network"
        description="Discover properties, connect with real estate professionals, and explore opportunities across the Philippines with PropertyConnect."
        canonical="/"
        robots="index,follow"
        jsonLd={[organizationJsonLd(), websiteJsonLd()]}
      />
      <PublicHeader />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#EAF4FF] to-white">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.06]" data-testid="hero-h1">
              <span className="text-[#17365D]">Your Next Real Estate Opportunity Starts </span>
              <span className="text-[#C79A3B]">With a Connection.</span>
            </h1>
            <p className="text-slate-600 text-base sm:text-lg mt-5 max-w-xl leading-relaxed" data-testid="hero-sub">Discover properties, share buyer needs, and connect with real estate professionals.</p>
            <div className="flex flex-wrap items-center gap-3 mt-7">
              <Button data-testid="hero-explore-btn" onClick={() => discover("For Sale")} className="bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white font-semibold rounded-xl h-12 px-6 text-sm">Explore Marketplace</Button>
              <Link to="/register" data-testid="hero-join-link"><Button variant="outline" className="border-[#0F2C59]/25 text-[#0F2C59] hover:bg-[#0F2C59]/5 rounded-xl h-12 px-6 text-sm font-semibold">Join the Network</Button></Link>
            </div>
            <p className="text-[11px] font-semibold tracking-[0.22em] text-[#C79A3B] uppercase mt-7">Connect · Match · Collaborate · Close</p>
          </div>
          <div className="block">
            <HeroVideo />
          </div>
        </div>
      </section>

      {/* FIND A PROPERTY — Buy / Rent / Stays search (criteria carry through to Marketplace) */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-8 relative z-10 pb-10">
          <form onSubmit={submitSearch} data-testid="find-property-form" className="rounded-2xl bg-white shadow-xl ring-1 ring-slate-200 p-4 sm:p-5">
            <div className="inline-flex rounded-xl bg-slate-100 p-1 mb-4">
              {TABS.map((t) => (
                <button key={t.key} type="button" data-testid={`find-tab-${t.label.toLowerCase()}`} onClick={() => setTab(t.key)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === t.key ? "bg-[#0F2C59] text-white" : "text-slate-600 hover:text-[#0F2C59]"}`}>
                  <t.icon className="h-4 w-4" />{t.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_auto] gap-3">
              <LocationBox value={loc} onPick={setLoc} testid="find-location" />
              <select data-testid="find-proptype" value={propType} onChange={(e) => setPropType(e.target.value)}
                className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#C79A3B]">
                <option value="">Any type</option>
                {PROPERTY_TYPE_GROUPS.map((g) => (
                  <optgroup key={g.label} label={g.label}>
                    {g.items.map((it) => <option key={it} value={it}>{propertyTypeLabel(it)}</option>)}
                  </optgroup>
                ))}
              </select>
              <select data-testid="find-price" value={priceIdx} onChange={(e) => setPriceIdx(Number(e.target.value))}
                className="h-12 rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#C79A3B]">
                {(PRICE_RANGES[tab] || []).map((r, i) => <option key={i} value={i}>{r.label}</option>)}
              </select>
              <Button type="submit" data-testid="find-search-btn" className="h-12 bg-[#0F2C59] hover:bg-[#0F2C59]/90 text-white rounded-xl font-semibold px-6 flex items-center gap-2">
                <Search className="h-4 w-4" /> <span>Search</span>
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* GOAL-BASED PUBLIC PLAYBOOK */}
      <PublicPlaybook />

      {/* FEATURED PROFESSIONALS (admin-managed placements; hidden when none are live) */}
      <FeaturedProsCarousel />

      {/* COMPACT LIVE NETWORK PREVIEW */}
      <NetworkPreview />

      {/* TRUST */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16 w-full" data-testid="trust-section">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
          <div>
            <span className="flex items-center justify-center h-12 w-12 rounded-xl bg-[#0F2C59]/5 mb-4"><ShieldCheck className="h-6 w-6 text-[#143163]" /></span>
            <h2 className="font-display font-bold text-[#0F2C59] text-2xl sm:text-3xl">Know Who You're Connecting With</h2>
            <p className="text-slate-600 text-sm sm:text-base mt-3 leading-relaxed">
              Explore published professional profiles with visible identity and verification signals before you reach out.
              Verification reflects information shared on the network and does not, on its own, guarantee any listing or transaction outcome.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {TRUST.map((x) => (
              <div key={x.t} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`trust-${x.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <x.icon className="h-5 w-5 text-[#C79A3B] mb-3" />
                <h3 className="font-display font-semibold text-[#0F2C59] text-sm">{x.t}</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROFESSIONAL TOOLS */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="tools-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-16">
          <div className="max-w-2xl mb-8">
            <p className="text-xs font-semibold tracking-[0.18em] text-[#C79A3B] uppercase mb-2">For Professionals</p>
            <h2 className="font-display font-bold text-[#0F2C59] text-2xl sm:text-3xl">Tools that help professionals work smarter</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl">
            {TOOLS.map((x) => (
              <div key={x.t} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`tool-${x.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <span className="flex items-center justify-center h-11 w-11 rounded-xl bg-[#0F2C59]/5 mb-4"><x.icon className="h-5 w-5 text-[#143163]" /></span>
                <h3 className="font-display font-bold text-[#0F2C59] text-lg mb-1.5">{x.t}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL INVITATION */}
      <section className="relative overflow-hidden bg-[#0F2C59]" data-testid="final-cta-section">
        <img src={IMG.dusk} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2C59] via-[#0F2C59]/85 to-[#143163]/70" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl leading-tight" data-testid="final-cta-h2">List. Connect. Collaborate.</h2>
          <p className="text-white/80 text-sm sm:text-base mt-3 max-w-xl mx-auto">Discover the network. Join when you're ready to participate.</p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <Link to="/register" data-testid="final-join-btn"><Button className="bg-[#C79A3B] hover:bg-[#b98d33] text-[#0F2C59] font-semibold rounded-xl h-12 px-7">Join the Network</Button></Link>
            <Link to="/marketplace" data-testid="final-explore-btn"><Button variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white rounded-xl h-12 px-7">Explore Marketplace</Button></Link>
          </div>
          <p className="text-[11px] font-semibold tracking-[0.22em] text-[#C79A3B]/90 uppercase mt-8">Connect · Match · Collaborate · Close</p>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
